/**
 * Stage 0D — current-state delivery for Birdseye and ShopFloor.
 *
 * Doctrine: realtime tells the client "something changed"; the database tells
 * it what is true. Nothing here reads an event payload as state — events only
 * decide WHETHER to refetch.
 */

/**
 * Ticket columns whose change alters what Birdseye / ShopFloor render. An
 * UPDATE that touches none of them does not refetch the board.
 *
 *   placement      bay_id, bay_assignment, parking_lot, parking_spot_id,
 *                  picked_up_at (a collected car leaves its spot)
 *   roster         scheduled_date, status, deleted_at, type
 *   people         assigned_tech_id, scheduled_tech_id ("Scheduled:" on a
 *                  parking card)
 *   timing         start_time, opti_finish, predicted_minutes,
 *                  actual_start_time (which ticket is "now" in a bay, and
 *                  the running timer)
 *   timer state    timer_status, pause_reason (the PAUSED badge and reason)
 *
 * Deliberately absent: vehicle_present (Birdseye derives presence from the
 * placement fields above and never reads the column), custody (not rendered
 * on the floor), and notes / customer contact fields.
 */
export const FLOOR_TICKET_FIELDS = [
  'scheduled_date',
  'assigned_tech_id',
  'start_time',
  'opti_finish',
  'status',
  'bay_id',
  'bay_assignment',
  'parking_lot',
  'parking_spot_id',
  'deleted_at',
  'type',
  'timer_status',
  'pause_reason',
  'actual_start_time',
  'scheduled_tech_id',
  'predicted_minutes',
  'picked_up_at',
] as const;

type Row = Record<string, unknown> | null | undefined;

/** Does this `tickets` change alter the rendered floor? INSERT/DELETE always do. */
export function floorTicketChangeMatters(payload: {
  eventType: string;
  new?: Row | Record<string, never>;
  old?: Row | Record<string, never>;
}): boolean {
  if (payload.eventType !== 'UPDATE') return true;
  const next = (payload.new ?? {}) as Record<string, unknown>;
  const prev = (payload.old ?? {}) as Record<string, unknown>;
  return FLOOR_TICKET_FIELDS.some((field) => next[field] !== prev[field]);
}

let topicSequence = 0;

/**
 * A realtime topic owned by exactly one subscription instance.
 *
 * realtime-js returns the EXISTING channel for a topic that is still in its
 * client — including one that is mid-`removeChannel` (state "leaving"), on
 * which `subscribe()` is a no-op — and when that leave completes it drops
 * EVERY channel with the topic. A page that rebuilt its subscription under
 * the same name (date change, shop change, remount) could therefore end up
 * bound to a dead channel and never hear another event. A per-instance topic
 * makes each subscription's lifecycle its own.
 */
export function uniqueChannelTopic(base: string): string {
  topicSequence += 1;
  return `${base}:${topicSequence}-${Math.random().toString(36).slice(2, 8)}`;
}

/**
 * What a channel status means for the rendered state.
 *   refetch — joined, or re-joined after an interruption: events may have been
 *             missed while not subscribed, so reload the truth.
 *   rebuild — closed abnormally: the client dropped the channel and will not
 *             re-join it; subscribe again on a fresh topic.
 *   none    — transient (the client re-joins on its own) or a normal close.
 */
export function realtimeRecoveryAction(status: string, err?: unknown): 'refetch' | 'rebuild' | 'none' {
  if (status === 'SUBSCRIBED') return 'refetch';
  if (status === 'CLOSED' && err !== undefined) return 'rebuild';
  return 'none';
}

/**
 * How long to wait before re-subscribing after the `attempt`-th consecutive
 * abnormal close: 3s, 6s, 12s, 24s, 48s — then stop (null). A join the server
 * keeps refusing must not become a re-subscribe-and-refetch loop; foreground
 * recovery still reloads the truth when the user comes back. A successful join
 * resets the count.
 */
export const MAX_REBUILD_ATTEMPTS = 5;
export function rebuildDelayMs(attempt: number): number | null {
  return attempt < MAX_REBUILD_ATTEMPTS ? 3000 * 2 ** attempt : null;
}
